import { Controller } from "@hotwired/stimulus"
import { getI18nMessage } from "../lib/i18n_helper"
import { showError as showErrorToast } from "../lib/platform_toast"

// Avatar Upload Controller
// Simple click-to-upload profile avatar with client-side validation and compression
//
// Features:
// - Click avatar to trigger file selection
// - Client-side validation (type, size)
// - Auto-compression for files over 2MB (Canvas API)
// - Resizes to 800px max width, JPEG 80% quality
// - Auto-submit on file selection
// - Turbo-powered AJAX upload
// - Toast notifications for feedback
//
// Usage:
// <form data-controller="avatar-upload" data-action="submit->avatar-upload#submit">
//   <button type="button" data-action="click->avatar-upload#pick">Edit Avatar</button>
//   <input type="file" data-avatar-upload-target="file" class="hidden" accept="image/*">
// </form>
export default class extends Controller {
  static targets = ["file", "preview"]
  static values = {
    maxSize: { type: Number, default: 2097152 }, // 2MB in bytes (BusinessConstants::MAX_AVATAR_SIZE)
    maxWidth: { type: Number, default: 800 },    // Max width for compression (800px)
    quality: { type: Number, default: 0.8 },     // JPEG quality (80%)
    lastUploadAt: String, // ISO8601 timestamp
    secondsUntilNext: Number // Seconds until next upload allowed (passed to dialog)
  }

  connect() {
    // Listen for file selection
    this.fileTarget.addEventListener("change", this.handleFileSelect.bind(this))
  }

  // Trigger file input click
  pick() {
    // Check daily limit BEFORE opening file picker
    if (this.checkDailyLimit()) {
      // Show toast message instead of opening file picker
      const errorMsg = getI18nMessage(
        this.element,
        'errorsDailyLimit',
        '每24小時只能更新一次頭像'
      )
      this.showError(errorMsg)
      return
    }

    // If no limit, proceed to open file picker
    this.fileTarget.click()
  }

  // Handle file selection
  async handleFileSelect(event) {
    const file = event.target.files[0]

    if (!file) return

    // Check daily limit FIRST (before file validation)
    if (this.checkDailyLimit()) {
      this.fileTarget.value = "" // Clear file selection
      this.showLimitExceededDialog()
      return
    }

    // Validate file type (not size - we'll compress if needed)
    if (!file.type.startsWith("image/")) {
      const errorMsg = getI18nMessage(
        this.element,
        'errorsNotImage',
        `${file.name} 不是圖片檔案`,
        { name: file.name }
      )
      this.showError(errorMsg)
      this.fileTarget.value = ""
      return
    }

    // If file is under limit, upload directly
    if (file.size <= this.maxSizeValue) {
      this.element.requestSubmit()
      return
    }

    // File is over 2MB - compress it
    try {
      const compressed = await this.compressImage(file)

      // Check if compressed file is still too large
      if (compressed.size > this.maxSizeValue) {
        const maxSizeMB = (this.maxSizeValue / 1024 / 1024).toFixed(1)
        const errorMsg = getI18nMessage(
          this.element,
          'errorsFileSize',
          `${file.name} 超過 ${maxSizeMB}MB 限制`,
          { name: file.name, size: maxSizeMB }
        )
        this.showError(errorMsg)
        this.fileTarget.value = ""
        return
      }

      // Replace file input with compressed file
      this.replaceFile(compressed)

      // Auto-submit form
      this.element.requestSubmit()
    } catch (error) {
      console.error("Image compression failed:", error)
      // Fallback: try original file validation
      if (!this.validateFile(file)) {
        this.fileTarget.value = ""
        return
      }
      this.element.requestSubmit()
    }
  }

  // Validate file before upload
  validateFile(file) {
    // Check file type
    if (!file.type.startsWith("image/")) {
      const errorMsg = getI18nMessage(
        this.element,
        'errorsNotImage',
        `${file.name} 不是圖片檔案`,
        { name: file.name }
      )
      this.showError(errorMsg)
      return false
    }

    // Check file size
    if (file.size > this.maxSizeValue) {
      const maxSizeMB = (this.maxSizeValue / 1024 / 1024).toFixed(1)
      const errorMsg = getI18nMessage(
        this.element,
        'errorsFileSize',
        `${file.name} 超過 ${maxSizeMB}MB 限制`,
        { name: file.name, size: maxSizeMB }
      )
      this.showError(errorMsg)
      return false
    }

    return true
  }

  // Compress image using Canvas API
  // Resizes to maxWidth and compresses to JPEG with specified quality
  // @param {File} file - Original image file
  // @return {Promise<File>} Compressed image file
  compressImage(file) {
    return new Promise((resolve, reject) => {
      const img = new Image()

      img.onload = () => {
        // Release object URL after loading
        URL.revokeObjectURL(img.src)

        const canvas = document.createElement('canvas')
        let { width, height } = img

        // Calculate new dimensions (maintain aspect ratio)
        if (width > this.maxWidthValue) {
          height = Math.round((height * this.maxWidthValue) / width)
          width = this.maxWidthValue
        }

        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        // Use high-quality image smoothing
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(img, 0, 0, width, height)

        // Convert to JPEG blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas toBlob failed'))
              return
            }

            // Create new File from blob with original name
            const compressedFile = new File(
              [blob],
              file.name.replace(/\.[^.]+$/, '.jpg'), // Change extension to .jpg
              { type: 'image/jpeg' }
            )

            resolve(compressedFile)
          },
          'image/jpeg',
          this.qualityValue
        )
      }

      img.onerror = () => {
        URL.revokeObjectURL(img.src)
        reject(new Error('Failed to load image'))
      }

      img.src = URL.createObjectURL(file)
    })
  }

  // Replace file input with compressed file
  // Uses DataTransfer API to set files on input
  // @param {File} file - Compressed file to set
  replaceFile(file) {
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    this.fileTarget.files = dataTransfer.files
  }

  // Show validation error via toast
  // Platform-aware: iOS uses native toast, Web/Android uses web toast
  showError(message) {
    showErrorToast(message)
  }

  // Handle successful submit (optional)
  submit(event) {
    // Turbo will handle the form submission automatically
    // We can add loading state here if needed
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("opacity-50", "pointer-events-none")
    }
  }

  // Check if daily upload limit exceeded
  // @return [Boolean] true if limit exceeded
  checkDailyLimit() {
    // If no last upload timestamp, allow upload
    if (!this.hasLastUploadAtValue || !this.lastUploadAtValue) {
      return false
    }

    try {
      const lastUpload = new Date(this.lastUploadAtValue)
      const now = new Date()
      const hoursSince = (now - lastUpload) / (1000 * 60 * 60)

      return hoursSince < 24
    } catch (error) {
      console.error("Failed to parse last upload timestamp:", error)
      return false // Allow upload on error
    }
  }

  // Show limit exceeded dialog via Turbo Frame
  showLimitExceededDialog() {
    // Show toast notification first
    const errorMsg = getI18nMessage(
      this.element,
      'errorsDailyLimit',
      '每24小時只能更新一次頭像'
    )
    this.showError(errorMsg)

    // Then load dialog content into turbo-frame
    import("@hotwired/turbo").then(({ Turbo }) => {
      Turbo.visit("/profile/avatar_limit_dialog", {
        frame: "avatar-limit-modal"
      })
    })
  }
}
